import type { StoryGraph, StoryGraphEdge, Scene } from '../@types';

export interface GraphValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  /**
   * Edge IDs that cannot be followed: a condition with no matching
   * ChoiceOption on the source scene, or a `from`/`to` naming no node.
   */
  danglingEdgeIds: string[];
}

/** What the validator needs to know about each scene (spec 010). */
export type GraphScene = Pick<Scene, 'name' | 'choices'>;

/**
 * The single rule set for a story graph (spec 010), used live by the editor
 * and by publish (spec 011). An empty graph is valid.
 */
export function validateGraph(
  graph: StoryGraph,
  scenesByStorySceneId: Record<number, GraphScene>,
): GraphValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const danglingEdgeIds: string[] = [];

  if (graph.nodes.length === 0) {
    return { valid: true, errors: [], warnings: [], danglingEdgeIds: [] };
  }

  const nodeIds = new Set(graph.nodes.map((n) => n.id));
  const dangling = (edge: StoryGraphEdge) => {
    if (!danglingEdgeIds.includes(edge.id)) danglingEdgeIds.push(edge.id);
  };

  // ── ERRORS: start node ──────────────────────────────────────────
  if (!graph.startNodeId || !nodeIds.has(graph.startNodeId)) {
    errors.push(
      graph.startNodeId
        ? `Start node ${graph.startNodeId} is not on the graph`
        : 'The story has no start scene',
    );
  }

  // ── ERRORS: edges whose endpoints name no node ──────────────────
  const outgoingEdges = new Map<string, StoryGraphEdge[]>();
  for (const node of graph.nodes) outgoingEdges.set(node.id, []);
  for (const edge of graph.edges) {
    const missing = [edge.from, edge.to].filter((id) => !nodeIds.has(id));
    if (missing.length > 0) {
      dangling(edge);
      errors.push(`Edge ${edge.id} points to a missing node (${missing.join(', ')})`);
      continue;
    }
    outgoingEdges.get(edge.from)!.push(edge);
  }

  // ── ERRORS: per-node scene, choice and edge rules ───────────────
  for (const node of graph.nodes) {
    if (node.type === 'end') continue;

    const scene = scenesByStorySceneId[node.storySceneId];
    if (!scene) {
      errors.push(`Node ${node.id} names a scene that is not in this story`);
      continue;
    }

    const choices = scene.choices ?? [];
    const nodeEdges = outgoingEdges.get(node.id) ?? [];
    const unconditional = nodeEdges.filter((e) => !e.condition);

    if (unconditional.length > 1) {
      errors.push(`Scene "${scene.name}" has more than one unconditional next edge`);
    }

    if (choices.length > 0) {
      if (unconditional.length > 0) {
        errors.push(
          `Scene "${scene.name}" has choices, so it cannot also have an unconditional next edge`,
        );
      }

      // Every choice needs exactly one outgoing edge.
      for (const option of choices) {
        const matching = nodeEdges.filter((e) => e.condition === option.id);
        const label = option.text ? `"${option.text}"` : '(no label)';
        if (matching.length === 0) {
          errors.push(
            `Scene "${scene.name}": option ${label} (id: ${option.id}) has no outgoing edge`,
          );
        } else if (matching.length > 1) {
          errors.push(
            `Scene "${scene.name}": choice ${label} (id: ${option.id}) has more than one outgoing edge`,
          );
        }
        if (!option.text || !option.text.trim()) {
          warnings.push(`Scene "${scene.name}": choice ${option.id} has no label`);
        }
      }
    }

    // Every conditional edge must match a current choice.
    for (const edge of nodeEdges) {
      if (edge.condition && !choices.some((c) => c.id === edge.condition)) {
        dangling(edge);
        errors.push(
          `Edge ${edge.id}: condition "${edge.condition}" has no matching option on scene "${scene.name}"`,
        );
      }
    }
  }

  // ── WARNINGS: reachability ───────────────────────────────────────
  const reachable = new Set<string>();
  if (graph.startNodeId && nodeIds.has(graph.startNodeId)) {
    const queue = [graph.startNodeId];
    reachable.add(graph.startNodeId);
    while (queue.length > 0) {
      const current = queue.shift()!;
      for (const edge of outgoingEdges.get(current) ?? []) {
        if (!reachable.has(edge.to)) {
          reachable.add(edge.to);
          queue.push(edge.to);
        }
      }
    }
  }

  for (const node of graph.nodes) {
    if (!reachable.has(node.id)) {
      warnings.push(`Node ${node.id} is unreachable from the start node`);
    }
  }

  // ── WARNINGS: no terminal node ──────────────────────────────────
  const hasTerminal = graph.nodes.some(
    (node) => node.type === 'end' || (outgoingEdges.get(node.id) ?? []).length === 0,
  );
  if (!hasTerminal) {
    warnings.push('Story has no terminal node — learners cannot reach an end state');
  }

  return { valid: errors.length === 0, errors, warnings, danglingEdgeIds };
}
